/**
 * Event deduplication utilities for aggregated calendar views.
 *
 * When the same meeting is invited to multiple family members, it creates
 * separate database records per calendar source. This utility consolidates
 * those duplicates into single events with multi-member indicators.
 *
 * ## Fuzzy Timestamp Matching
 *
 * Calendar APIs (Google, Microsoft) return timestamps with millisecond precision.
 * When the same event is synced to different calendars, timestamps may differ
 * slightly (e.g., "2026-01-15T19:30:00.000Z" vs "2026-01-15T19:30:00.123Z").
 *
 * To handle this, deduplication keys round timestamps to the nearest minute.
 * Events with the same title and times within the same minute are considered
 * duplicates. This provides reliable matching without false positives (different
 * events rarely share title + same minute).
 *
 * Used by:
 * - apps/web/src/lib/key-event.ts (getTopEvents for day preview cards)
 * - apps/web/src/lib/upcoming-events.ts (getUpcomingEvents sidebar)
 * - apps/web/src/pages/Calendar.tsx (WeekView, MonthView)
 *
 * NOT used by:
 * - Day view columns (per-member display is correct behavior)
 */

import type { CalendarEvent, Member } from '../types/calendar';

/**
 * Round timestamp to nearest minute for fuzzy matching.
 * Calendar APIs return ms precision; same event on different calendars
 * may have slightly different timestamps.
 */
function roundTimestampToMinute(timestamp: string): string {
  const date = new Date(timestamp);
  date.setSeconds(0, 0);
  return date.toISOString().slice(0, 16); // "YYYY-MM-DDTHH:mm"
}

/**
 * Represents a deduplicated event with all members who have this event.
 */
export interface DeduplicatedEvent extends CalendarEvent {
  /** All members who have this event on their calendar */
  members: Member[];
  /** True if multiple members share this event */
  isShared: boolean;
}

/**
 * Generate a deduplication key for an event.
 * Events with the same key are considered duplicates.
 *
 * Match criteria: title (case-insensitive, trimmed) + startTime + endTime
 * Times are rounded to nearest minute for fuzzy matching across calendar sources.
 */
export function getEventDedupeKey(event: CalendarEvent): string {
  const roundedStart = roundTimestampToMinute(event.startTime);
  const roundedEnd = roundTimestampToMinute(event.endTime);
  return `${event.title.toLowerCase().trim()}|${roundedStart}|${roundedEnd}`;
}

/**
 * Deduplicate events from multiple members.
 * Returns events with member arrays for shared events.
 *
 * @param dayEvents - Events grouped by member ID
 * @param members - Array of family members for color lookup
 * @returns Deduplicated events with member info attached
 */
export function deduplicateEvents(
  dayEvents: Record<string, CalendarEvent[]>,
  members: Member[]
): DeduplicatedEvent[] {
  const memberMap = new Map(members.map((m) => [m.id, m]));
  const dedupeMap = new Map<string, DeduplicatedEvent>();

  for (const [memberId, events] of Object.entries(dayEvents)) {
    const member = memberMap.get(memberId);
    if (!member) continue;

    for (const event of events) {
      const key = getEventDedupeKey(event);

      if (dedupeMap.has(key)) {
        // Add member to existing deduplicated event
        const existing = dedupeMap.get(key)!;
        if (!existing.members.some((m) => m.id === member.id)) {
          existing.members.push(member);
          existing.isShared = true;
        }
      } else {
        // Create new deduplicated event
        dedupeMap.set(key, {
          ...event,
          member, // Primary member (first encountered)
          members: [member],
          isShared: false,
        });
      }
    }
  }

  // Sort members alphabetically for consistent display
  for (const event of dedupeMap.values()) {
    event.members.sort((a, b) => a.name.localeCompare(b.name));
  }

  return Array.from(dedupeMap.values());
}

/**
 * Deduplicate a flat array of events (used for upcoming events).
 * Events must already have member info attached.
 *
 * @param events - Flat array of events with member info
 * @returns Deduplicated events with member arrays
 */
export function deduplicateEventArray<T extends CalendarEvent>(
  events: T[]
): (T & { members: Member[]; isShared: boolean })[] {
  const dedupeMap = new Map<string, T & { members: Member[]; isShared: boolean }>();

  for (const event of events) {
    const key = getEventDedupeKey(event);
    const member = event.member;

    if (dedupeMap.has(key)) {
      const existing = dedupeMap.get(key)!;
      if (member && !existing.members.some((m) => m.id === member.id)) {
        existing.members.push(member);
        existing.isShared = true;
      }
    } else {
      dedupeMap.set(key, {
        ...event,
        members: member ? [member] : [],
        isShared: false,
      });
    }
  }

  // Sort members alphabetically for consistent display
  for (const event of dedupeMap.values()) {
    event.members.sort((a, b) => a.name.localeCompare(b.name));
  }

  return Array.from(dedupeMap.values());
}
